import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import SessionCard from './SessionCard';
import {
  STATUS_TABS,
  formatDate,
  monthSeries,
  sessionsQuery,
} from './sessionHistory';

/**
 * L'HISTORIQUE DES SÉANCES D'UN ANIMATEUR.
 *
 * Deux requêtes, et c'est délibéré : `/my_sessions_stats` répond à « qu'est-ce
 * que j'ai animé » et `/my_sessions` à « et cette séance-là, c'était quoi ».
 * Les agrégats ne bougent pas quand on change de page de liste, et la liste ne
 * fait pas repasser une agrégation à chaque clic.
 *
 * Les lignes arrivent déjà projetées côté serveur : cet écran ne charge jamais
 * une partie complète, même pour une séance qu'il affiche en détail.
 */

const PAGE_SIZE = 10;
const MONTHS_SHOWN = 12;

const OWNER_OPTIONS = [
  { value: '', label: 'Mes séances' },
  { value: '*', label: 'Toutes les séances' },
  { value: 'none', label: 'Séances sans animateur' },
];

const Tile = ({ value, label, hint }) => (
  <div className="history-tile">
    <div className="history-tile-value">{value}</div>
    <div className="history-tile-label">{label}</div>
    {hint && <div className="history-tile-hint">{hint}</div>}
  </div>
);

/**
 * L'activité mois par mois. Les mois vides sont dessinés (cf. `monthSeries`) :
 * un creux est une information, et coller deux mois actifs séparés par un
 * semestre de silence raconterait une régularité qui n'a pas eu lieu.
 */
const MonthBars = ({ byMonth }) => {
  const series = useMemo(() => monthSeries(byMonth, MONTHS_SHOWN), [byMonth]);
  const max = Math.max(1, ...series.map((row) => row.count));
  if (!series.some((row) => row.count > 0)) return null;

  return (
    <div className="history-months">
      {series.map((row, index) => (
        <div
          className="history-month"
          key={row.month}
          title={`${row.label} : ${row.count} séance${row.count > 1 ? 's' : ''}`}
        >
          <div className="history-month-track">
            <div
              className="history-month-fill"
              style={{ height: `${Math.round((100 * row.count) / max)}%` }}
            />
          </div>
          {/* Un libellé sur trois : douze « sept. 26 » côte à côte ne se lisent
              pas. Le survol donne le mois exact de chaque barre. */}
          <span className="history-month-label">{index % 3 === 0 ? row.label : ''}</span>
        </div>
      ))}
    </div>
  );
};

const ConfigBars = ({ byConfig }) => {
  const rows = (byConfig || []).slice(0, 6);
  const max = Math.max(1, ...rows.map((row) => row.count));
  if (!rows.length) return null;

  return (
    <div className="history-config-list">
      {rows.map((row) => (
        <div className="history-config-row" key={row.config_id}>
          <span className="history-config-label" title={row.name || row.config_id}>
            {row.name || row.config_id}
          </span>
          <span className="history-config-track">
            <span
              className="history-config-fill"
              style={{ width: `${Math.round((100 * row.count) / max)}%` }}
            />
          </span>
          <span className="history-config-value">{row.count}</span>
        </div>
      ))}
    </div>
  );
};

const SessionHistoryPanel = ({ token, setToken, isAdmin }) => {
  const navigate = useNavigate();

  const [sessions, setSessions] = useState([]);
  const [total, setTotal] = useState(0);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState(null);

  const [status, setStatus] = useState('');
  const [configId, setConfigId] = useState('');
  const [owner, setOwner] = useState('');
  const [includeTests, setIncludeTests] = useState(false);

  // Jeton de requête. `/my_sessions` est relancé à chaque changement de filtre,
  // et une réponse lente d'un filtre abandonné ne doit pas écraser celle du
  // filtre courant.
  const requestId = useRef(0);

  // LE JETON EST LU DANS UNE REF, ET NON CAPTURÉ. Le serveur glisse un jeton
  // rafraîchi dans les réponses proches de l'expiration : capturé, il changerait
  // l'identité de `call`, donc celle de `load`, donc relancerait l'effet — un
  // rafraîchissement de jeton rechargeait tout l'historique. `setToken` a le
  // même problème pour une autre raison : `AuthContext` le redéfinit à chaque
  // rendu du provider.
  const tokenRef = useRef(token);
  const setTokenRef = useRef(setToken);
  tokenRef.current = token;
  setTokenRef.current = setToken;

  const call = useCallback(async (path, options) => {
    const response = await fetch(`${process.env.REACT_APP_API_URL}${path}`, {
      ...options,
      headers: {
        ...(options?.headers),
        Authorization: 'Bearer ' + tokenRef.current,
      },
    });
    if (response.status === 401 || response.status === 422) {
      navigate('/login');
      return null;
    }
    const data = await response.json();
    // Poser le jeton rafraîchi évite qu'une consultation d'historique un peu
    // longue finisse sur l'écran de connexion (cf. `refresh_expiring_jwts`).
    if (data && data.access_token) setTokenRef.current(data.access_token);
    if (!response.ok) throw new Error(data?.error || data?.message || 'Requête refusée');
    return data;
  }, [navigate]);

  /**
   * LES AGRÉGATS NE DÉPENDENT PAS DES FILTRES DE LISTE.
   *
   * « Séances animées », « Personnes formées » et les deux graphes décrivent
   * TOUT l'historique : les recharger en changeant d'onglet d'état ou
   * d'atelier faisait repasser une agrégation `$facet` pour réafficher des
   * chiffres identiques — et faisait clignoter des tuiles que le clic ne
   * concernait pas. Seuls le périmètre (`owner`) et l'inclusion des séances de
   * test les changent.
   */
  const loadStats = useCallback(async () => {
    try {
      const aggregates = await call(`/my_sessions_stats${sessionsQuery({ owner, includeTests })}`);
      if (aggregates) setStats(aggregates);
    } catch (statsError) {
      // Les tuiles manquantes ne valent pas de masquer la liste : l'erreur du
      // listing, elle, est affichée.
      console.error('Statistiques indisponibles :', statsError);
    }
  }, [call, owner, includeTests]);

  const loadSessions = useCallback(async (skip) => {
    const id = requestId.current + 1;
    requestId.current = id;
    if (skip) setLoadingMore(true);
    else setLoading(true);
    setError(null);

    try {
      const query = sessionsQuery({ status, configId, owner, includeTests, limit: PAGE_SIZE, skip });
      const list = await call(`/my_sessions${query}`);

      if (requestId.current !== id || !list) return;
      setSessions((current) => (skip ? [...current, ...list.sessions] : list.sessions));
      setTotal(list.total);
    } catch (requestError) {
      if (requestId.current === id) setError(requestError.message);
    } finally {
      if (requestId.current === id) {
        setLoading(false);
        setLoadingMore(false);
      }
    }
  }, [call, status, configId, owner, includeTests]);

  useEffect(() => {
    loadSessions(0);
  }, [loadSessions]);

  useEffect(() => {
    loadStats();
  }, [loadStats]);

  const saveMeta = async (gameId, payload) => {
    try {
      const data = await call(`/session/${gameId}/meta`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (!data || !data.success) return data?.message || "L'enregistrement a échoué.";
      // RECHARGEMENT COMPLET, et pas une retouche locale de la ligne : une
      // séance qu'on vient de marquer « test » sort du listing, et son
      // effectif entre dans les personnes formées. Retoucher la ligne sur
      // place laisserait les tuiles mentir. C'est le seul geste qui touche
      // vraiment aux deux.
      await Promise.all([loadSessions(0), loadStats()]);
      return null;
    } catch (saveError) {
      return saveError.message;
    }
  };

  // « SUPPRIMER » N'EFFACE RIEN : le serveur pose `is_archived` et la séance
  // sort de tout ce qui est listé ou compté. Même rechargement complet que la
  // fiche, et pour la même raison — la ligne disparaît ET les tuiles bougent.
  const archiveSession = async (gameId) => {
    try {
      const data = await call(`/session/${gameId}/archive`, { method: 'POST' });
      if (!data || !data.success) return data?.message || 'La suppression a échoué.';
      await Promise.all([loadSessions(0), loadStats()]);
      return null;
    } catch (archiveError) {
      return archiveError.message;
    }
  };

  // Les ateliers réellement animés, avec leur nombre de séances — et non la
  // liste des ateliers autorisés : on ne filtre que ce qui existe dans
  // l'historique, comme les facettes du sélecteur de mesures.
  const configOptions = stats?.by_config || [];
  const remaining = Math.max(0, total - sessions.length);
  // Le SEUL cas où il n'y a rien à montrer : le premier chargement. Tous les
  // suivants gardent la liste précédente sous le voile.
  const firstLoad = loading && sessions.length === 0 && !error;

  return (
    <div className="history-panel">
      <div className="profile-top-container">
        <h2 className="color-blue">Mes séances</h2>
        {stats?.first_session && (
          <span className="history-muted">Depuis le {formatDate(stats.first_session)}</span>
        )}
      </div>

      {stats && (
        <>
          <div className="history-tiles">
            <Tile value={stats.nb_sessions} label="Séances animées" />
            <Tile value={stats.nb_participants} label="Personnes formées"
                  hint="Somme des effectifs renseignés sur les fiches" />
            <Tile value={stats.nb_finished} label="Séances terminées"
                  hint={`sur ${stats.nb_sessions} lancées`} />
          </div>

          {(stats.by_month?.length > 0 || configOptions.length > 0) && (
            <div className="history-overview">
              <div className="history-overview-block">
                <div className="history-block-title">Activité sur 12 mois</div>
                <MonthBars byMonth={stats.by_month} />
              </div>
              <div className="history-overview-block">
                <div className="history-block-title">Par atelier</div>
                <ConfigBars byConfig={configOptions} />
              </div>
            </div>
          )}
        </>
      )}

      <div className="history-filters">
        <div className="history-tabs">
          {STATUS_TABS.map((tab) => (
            <button
              key={tab.value || 'all'}
              className={`history-tab${status === tab.value ? ' history-tab-active' : ''}`}
              onClick={() => setStatus(tab.value)}
            >
              {tab.label}
            </button>
          ))}
        </div>

        <select className="history-select" value={configId} onChange={(e) => setConfigId(e.target.value)}>
          <option value="">Tous les ateliers</option>
          {configOptions.map((row) => (
            <option key={row.config_id} value={row.config_id}>
              {(row.name || row.config_id)} ({row.count})
            </option>
          ))}
        </select>

        {isAdmin && (
          <select className="history-select" value={owner} onChange={(e) => setOwner(e.target.value)}>
            {OWNER_OPTIONS.map((option) => (
              <option key={option.value || 'mine'} value={option.value}>{option.label}</option>
            ))}
          </select>
        )}

        <label className="history-check">
          <input
            type="checkbox"
            checked={includeTests}
            onChange={(event) => setIncludeTests(event.target.checked)}
          />
          Inclure les séances de test
        </label>
      </div>

      {error && <div className="history-error">{error}</div>}

      {/* LA LISTE COURANTE RESTE À L'ÉCRAN PENDANT LE RECHARGEMENT.
          Elle était auparavant remplacée par l'écran de chargement dès le
          début de la requête : cliquer sur un onglet faisait s'effondrer la
          page à la hauteur d'un encadré, puis la faisait regrandir à
          l'arrivée des lignes — et le défilement sautait avec. Le seul cas où
          il n'y a littéralement rien à montrer est le tout premier chargement.
          C'est la même leçon que `GamePage` a déjà apprise sur les pages de
          jeu : le voile se pose PAR-DESSUS, il ne remplace rien. */}
      {firstLoad ? (
        <div className="history-placeholder">Chargement de l'historique…</div>
      ) : sessions.length === 0 ? (
        <div className="history-placeholder">
          <strong>Aucune séance à afficher.</strong>
          <p>
            {status || configId || !includeTests
              ? 'Aucune séance ne correspond à ces filtres.'
              : "Les séances que vous animerez apparaîtront ici."}
          </p>
          {/* DIT FRANCHEMENT CE QUI MANQUE. Les parties lancées avant la mise
              en place de l'historique n'ont pas d'animateur enregistré, et
              rien ne permet de le retrouver : un animateur qui a mené trente
              ateliers et lit « 0 » doit savoir que ce n'est pas une panne. */}
          <p className="history-muted">
            Les séances lancées avant la mise en place de l'historique n'ont pas d'animateur
            enregistré et n'y figurent pas.
          </p>
        </div>
      ) : (
        <>
          <div className={`history-list${loading ? ' history-list-pending' : ''}`}>
            {sessions.map((session) => (
              <SessionCard
                key={session.game_id}
                session={session}
                onOpen={(target) => navigate(`/game/${target.game_id}`)}
                onSave={saveMeta}
                onArchive={archiveSession}
              />
            ))}
          </div>

          {remaining > 0 && (
            <button
              className="history-button history-more"
              onClick={() => loadSessions(sessions.length)}
              disabled={loadingMore || loading}
            >
              {loadingMore ? 'Chargement…' : `Charger la suite (${remaining} restantes)`}
            </button>
          )}
        </>
      )}
    </div>
  );
};

export default SessionHistoryPanel;